import { Card, Loader, Pagination, Select, Tag } from "@caprikorn/dynamo-ui";
import { type FC, useState } from "react";

import { ErrorTemplate } from "@components/ErrorTemplate/ErrorTemplate";
import { Header } from "@components/Header/Header";
import { TicketSimple } from "@icons";
import { ROUTES } from "@router/router.constants";
import type { Ticket, TicketStatus } from "@services/apiEcms/apiEcms.types";
import { useGetTechnicalRequests } from "@services/apiEcms/useGetTechnicalRequests";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { TicketDrawer } from "./TicketDrawer/TicketDrawer";
import classes from "./Tickets.module.css";
import { TICKET_TAG_COLORS } from "./Tickets.utils";

export const Tickets: FC = () => {
  const { t } = useCustomTranslation(["main", "common"]);

  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<TicketStatus[]>([]);
  const [activeTicketId, setActiveTicketId] = useState<number>();

  const { isLoading, data, isError } = useGetTechnicalRequests({
    page: currentPage,
    size: pageSize,
    filters: filterStatus,
  });

  const onPageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setActiveTicketId(undefined);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setActiveTicketId(undefined);
  };

  const handleClick = (ticket: Ticket) => {
    setActiveTicketId(ticket.id);
  };

  return (
    <>
      <Header
        breadcrumbPaths={[
          {
            pathName: t("ticket.breadcrumb"),
            path: ROUTES.FRESHDESK_TICKETS,
            pathIcon: TicketSimple,
          },
        ]}
      />

      {activeTicketId !== undefined && (
        <TicketDrawer
          onClose={() => setActiveTicketId(undefined)}
          ticketId={activeTicketId}
        />
      )}

      <div className={classes.filter}>
        <Select
          isMultiple
          label={t("ticket.select.label")}
          onChange={(filter: string[]) => {
            setFilterStatus(filter as TicketStatus[]);
            setActiveTicketId(undefined);
          }}
          value={filterStatus}
        >
          <Select.Trigger
            labelPosition="inside"
            placeholder={t("ticket.select.placeholder")}
          />
          <Select.Dropdown>
            <Select.Options>
              <Select.Option label={t("ticket.select.closed")} value="5" />
              <Select.Option label={t("ticket.select.waiting")} value="3" />
              <Select.Option label={t("ticket.select.open")} value="2" />
            </Select.Options>
          </Select.Dropdown>
        </Select>
      </div>

      <div className={classes.tickets}>
        {isLoading && (
          <div className={classes.loading}>
            <Loader />
          </div>
        )}

        {isError && (
          <div className={classes.error}>
            <ErrorTemplate title={t("errors.data", { ns: "common" })} />
          </div>
        )}

        {data?.tickets.length === 0 && <p>{t("ticket.empty")}</p>}

        {data && data.tickets.length > 0 && (
          <ul className={classes.list}>
            {data.tickets.map((ticket) => (
              <Card
                key={ticket.id}
                aria-label={t("ticket.card.ariaLabel", { id: ticket.id })}
                as="li"
                className={classes.ticket}
                data-testid={`TicketCard-${ticket.id}`}
                onClick={() => handleClick(ticket)}
                role="button"
                tabIndex={0}
              >
                <div className={classes.informations}>
                  <p>{t("ticket.card.id", { id: ticket.id })}</p>

                  <h5 className="font-title-sm">
                    {ticket.subject || t("notApplicable", { ns: "common" })}
                  </h5>

                  <p>
                    {t("ticket.card.creationDate", {
                      date: new Date(ticket.createdAt),
                      formatParams: { date: { dateStyle: "short" } },
                    })}
                  </p>
                </div>

                <Tag
                  color={TICKET_TAG_COLORS[ticket.status] ?? "default"}
                  label={t("ticket.tag", { context: String(ticket.status) })}
                  size="sm"
                />
              </Card>
            ))}
          </ul>
        )}
      </div>

      {data?.pagination && data.tickets.length !== 0 && (
        <Pagination
          className={classes.pagination}
          currentPage={data.pagination.currentPage}
          currentPageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          pageSizes={[15, 30, 50, 75, 100]}
          pageSizeSelectorLabel={t("ticket.pagination.placeholder")}
          totalPages={Math.ceil(data.pagination.recordsCount / pageSize)}
          withPageSizeSelector
        />
      )}
    </>
  );
};
